/**
 * UniverseLayout, Shared living universe background wrapper.
 * Wraps any page to give it the same animated sky, particles, planets,
 * sparkles, comets, clouds, meteors, and mouse trails as the homepage.
 *
 * Usage: <UniverseLayout>{children}</UniverseLayout>
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  DayNightCycle, ParallaxStars, ForegroundSparkles, ShootingStars
} from "./shared";
import { getAppTheme, getAppThemeConfig, APP_THEME_EVENT } from "../lib/appTheme";

/* -------- Time-based sky gradient -------- */
export function useSkyTheme() {
  const h = new Date().getHours();
  if (h >= 6 && h < 10) return { period: "dawn", sky: "from-indigo-950 via-rose-900 to-amber-800", starOpacity: 0.2 };
  if (h >= 10 && h < 17) return { period: "day", sky: "from-sky-400 via-blue-500 to-indigo-600", starOpacity: 0 };
  if (h >= 17 && h < 20) return { period: "dusk", sky: "from-indigo-900 via-purple-800 to-orange-700", starOpacity: 0.4 };
  return { period: "night", sky: "from-[#141B2D] via-indigo-950 to-[#141B2D]", starOpacity: 1 };
}

/* -------- Mouse sparkle trail -------- */
const MouseSparkleTrail = () => {
  const [sparkles, setSparkles] = useState([]);
  const idRef = useRef(0);
  useEffect(() => {
    const handler = (e) => {
      if (Math.random() > 0.3) return;
      const id = ++idRef.current;
      const colors = ["#FBBF24", "#A78BFA", "#34D399", "#F472B6", "#38BDF8", "#FB923C"];
      setSparkles(prev => [...prev.slice(-8), { id, x: e.clientX, y: e.clientY, color: colors[id % colors.length], size: 4 + Math.random() * 6 }]);
      setTimeout(() => setSparkles(prev => prev.filter(s => s.id !== id)), 800);
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);
  return (
    <div className="fixed inset-0 pointer-events-none z-[999]">
      {sparkles.map(s => (
        <div key={s.id} className="absolute" style={{
          left: s.x - s.size / 2, top: s.y - s.size / 2,
          width: s.size, height: s.size, borderRadius: "50%",
          background: s.color, boxShadow: `0 0 ${s.size * 2}px ${s.color}`,
          animation: "mouse-sparkle 0.8s ease-out forwards",
        }} />
      ))}
    </div>
  );
};

/* -------- Animated floating orbs -------- */
const AnimatedOrbs = ({ colorsOverride }) => {
  // Softened so the decorative orbs recede behind content (they were
  // overlapping text + the empty avatar circle on Settings / Parent Corner,
  // hurting legibility). Also nudged the two center orbs toward the edges.
  const colors = colorsOverride || [
    "rgba(167,139,250,0.22)", "rgba(251,146,60,0.18)",
    "rgba(236,72,153,0.15)", "rgba(34,211,238,0.18)", "rgba(52,211,153,0.15)"
  ];
  const configs = [
    { size: 350, top: "-8%", right: "-8%", anim: "orb-float-1 25s ease-in-out infinite" },
    { size: 300, bottom: "3%", left: "-12%", anim: "orb-float-2 30s ease-in-out infinite" },
    { size: 250, top: "28%", left: "72%", anim: "orb-float-3 35s ease-in-out infinite" },
    { size: 280, top: "55%", right: "-6%", anim: "orb-float-1 28s ease-in-out infinite reverse" },
    { size: 200, top: "8%", left: "12%", anim: "orb-float-2 22s ease-in-out infinite reverse" },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
      {configs.map((c, i) => (
        <div key={i} className="sky-orb" style={{
          width: c.size, height: c.size,
          top: c.top, bottom: c.bottom, left: c.left, right: c.right,
          background: `radial-gradient(circle, ${colors[i]}, ${colors[i].replace(/[\d.]+\)$/, '0.05)')}, transparent 70%)`,
          animation: c.anim,
        }} />
      ))}
    </div>
  );
};

/* -------- Light rays -------- */
const LightRays = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[3]">
    <div className="light-ray" style={{ left: "10%", animationDelay: "0s" }} />
    <div className="light-ray" style={{ left: "35%", animationDelay: "-5s" }} />
    <div className="light-ray" style={{ left: "60%", animationDelay: "-10s" }} />
    <div className="light-ray" style={{ left: "85%", animationDelay: "-15s" }} />
  </div>
);

/* -------- Animated wave ribbons -------- */
const WaveRibbons = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
    <svg className="absolute w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 800">
      <defs>
        <linearGradient id="uwave1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(139,92,246,0)" /><stop offset="30%" stopColor="rgba(139,92,246,0.15)" /><stop offset="50%" stopColor="rgba(236,72,153,0.2)" /><stop offset="70%" stopColor="rgba(251,146,60,0.15)" /><stop offset="100%" stopColor="rgba(251,146,60,0)" />
        </linearGradient>
        <linearGradient id="uwave2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(34,211,238,0)" /><stop offset="30%" stopColor="rgba(34,211,238,0.12)" /><stop offset="50%" stopColor="rgba(59,130,246,0.18)" /><stop offset="70%" stopColor="rgba(139,92,246,0.12)" /><stop offset="100%" stopColor="rgba(167,139,250,0)" />
        </linearGradient>
      </defs>
      <path fill="url(#uwave1)" opacity="0.6">
        <animate attributeName="d" dur="18s" repeatCount="indefinite" values="M0,200 C240,120 480,280 720,200 C960,120 1200,280 1440,200 L1440,320 C1200,400 960,240 720,320 C480,400 240,240 0,320 Z;M0,250 C240,170 480,330 720,250 C960,170 1200,330 1440,250 L1440,370 C1200,450 960,290 720,370 C480,450 240,290 0,370 Z;M0,200 C240,120 480,280 720,200 C960,120 1200,280 1440,200 L1440,320 C1200,400 960,240 720,320 C480,400 240,240 0,320 Z" />
      </path>
      <path fill="url(#uwave2)" opacity="0.5">
        <animate attributeName="d" dur="22s" repeatCount="indefinite" values="M0,450 C240,370 480,530 720,450 C960,370 1200,530 1440,450 L1440,570 C1200,650 960,490 720,570 C480,650 240,490 0,570 Z;M0,400 C240,320 480,480 720,400 C960,320 1200,480 1440,400 L1440,520 C1200,600 960,440 720,520 C480,600 240,440 0,520 Z;M0,450 C240,370 480,530 720,450 C960,370 1200,530 1440,450 L1440,570 C1200,650 960,490 720,570 C480,650 240,490 0,570 Z" />
      </path>
    </svg>
  </div>
);

/* -------- Floating planets -------- */
const FloatingPlanets = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 4 }}>
    <div className="absolute" style={{ top: "10%", right: "5%", animation: "planet-orbit 90s ease-in-out infinite" }}>
      <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full" style={{ background: "radial-gradient(circle at 32% 32%, #E9D5FF 0%, #A78BFA 30%, #6D28D9 70%, #3B0764 100%)", boxShadow: "0 0 60px rgba(167,139,250,0.5), 0 0 120px rgba(109,40,217,0.25), inset -10px -10px 30px rgba(0,0,0,0.4), inset 4px 4px 15px rgba(255,255,255,0.1)", opacity: 0.75 }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 sm:w-64 h-8 rounded-full border-2 border-purple-300/25 -rotate-12" style={{ boxShadow: "0 0 20px rgba(167,139,250,0.15)" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[42%] w-48 sm:w-56 h-5 rounded-full border border-purple-200/15 -rotate-12" />
    </div>
    <div className="absolute" style={{ bottom: "32%", left: "3%", animation: "planet-orbit 70s ease-in-out infinite reverse" }}>
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" style={{ background: "radial-gradient(circle at 38% 28%, #FEF3C7 0%, #FBBF24 35%, #B45309 75%, #78350F 100%)", boxShadow: "0 0 50px rgba(251,191,36,0.45), 0 0 100px rgba(180,83,9,0.2), inset -6px -6px 20px rgba(0,0,0,0.35), inset 3px 3px 10px rgba(255,255,255,0.15)", opacity: 0.7 }} />
    </div>
    <div className="absolute" style={{ top: "50%", right: "8%", animation: "planet-orbit 110s ease-in-out -40s infinite" }}>
      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full" style={{ background: "radial-gradient(circle at 35% 30%, #CFFAFE 0%, #06B6D4 40%, #0E7490 75%, #164E63 100%)", boxShadow: "0 0 40px rgba(6,182,212,0.5), 0 0 80px rgba(14,116,144,0.2), inset -4px -4px 15px rgba(0,0,0,0.35), inset 2px 2px 8px rgba(255,255,255,0.1)", opacity: 0.65 }} />
    </div>
  </div>
);

/* -------- Bold sparkles -------- */
const BoldSparkles = ({ isNight = false }) => {
  const count = isNight ? 60 : 40;
  const colors = ["#fff", "#C4B5FD", "#FDE68A", "#A5F3FC", "#FBCFE8", "#BBF7D0"];
  const stars = useMemo(() => Array.from({ length: count }, (_, i) => ({
    x: Math.random() * 100, y: Math.random() * 100,
    size: isNight ? 2 + Math.random() * 5 : 2 + Math.random() * 4,
    delay: Math.random() * 6, dur: 2 + Math.random() * 3,
    color: colors[i % colors.length],
  })), [count, isNight]);
  return (
    <div className="absolute inset-0 pointer-events-none z-[3]">
      {stars.map((s, i) => (
        <div key={i} className="absolute" style={{
          width: s.size, height: s.size, top: `${s.y}%`, left: `${s.x}%`,
          background: s.color, borderRadius: "50%",
          boxShadow: `0 0 ${s.size * 3}px ${s.color}, 0 0 ${s.size * 6}px ${s.color}50`,
          // Combined shorthand to silence React's mixed shorthand warning.
          animation: `star-pulse ${s.dur}s ease-in-out ${s.delay}s infinite`,
        }} />
      ))}
    </div>
  );
};

/* -------- Horizon glow -------- */
const HorizonGlow = () => (
  <div className="absolute bottom-0 left-0 w-full h-[35%] pointer-events-none z-[1] overflow-hidden">
    <div className="absolute bottom-0 left-0 w-full h-full" style={{
      background: "linear-gradient(0deg, rgba(251,146,60,0.25) 0%, rgba(236,72,153,0.15) 30%, rgba(139,92,246,0.08) 60%, transparent 100%)",
      animation: "aurora-shift 25s ease-in-out infinite",
    }} />
  </div>
);

/* -------- Gradient mesh -------- */
const GradientMesh = () => (
  <div className="absolute inset-0 pointer-events-none z-[1] overflow-hidden">
    <div className="absolute w-full h-full" style={{
      background: `radial-gradient(ellipse 80% 50% at 20% 30%, rgba(139,92,246,0.15), transparent), radial-gradient(ellipse 60% 60% at 80% 20%, rgba(236,72,153,0.12), transparent), radial-gradient(ellipse 70% 50% at 50% 80%, rgba(251,146,60,0.10), transparent), radial-gradient(ellipse 50% 40% at 70% 60%, rgba(34,211,238,0.10), transparent)`,
      animation: "mesh-shift 30s ease-in-out infinite",
    }} />
  </div>
);

/* -------- Pulsing rings -------- */
const PulsingRings = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]">
    {[0, 1, 2].map(i => (
      <div key={i} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border" style={{
        width: `${300 + i * 200}px`, height: `${300 + i * 200}px`,
        borderColor: ["rgba(139,92,246,0.12)", "rgba(236,72,153,0.08)", "rgba(34,211,238,0.06)"][i],
        animation: `ring-pulse ${8 + i * 4}s ease-in-out ${i * -2}s infinite`,
      }} />
    ))}
  </div>
);

/* -------- Meteor trails -------- */
const MeteorTrails = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[4]">
    {[0, 1, 2, 3, 4].map(i => (
      <div key={i} className="meteor-trail" style={{ top: `${3 + i * 18}%`, left: `${-10 + i * 22}%`, animationDelay: `${i * 5}s` }} />
    ))}
  </div>
);

/* -------- Giant periodic comet -------- */
const GiantComet = () => {
  const [visible, setVisible] = useState(false);
  const [fromTop, setFromTop] = useState(15);
  const timeoutRef = useRef(null);
  useEffect(() => {
    const trigger = () => {
      setFromTop(8 + Math.random() * 50);
      setVisible(true);
      setTimeout(() => setVisible(false), 3500);
      timeoutRef.current = setTimeout(trigger, 20000 + Math.random() * 15000);
    };
    timeoutRef.current = setTimeout(trigger, 5000 + Math.random() * 5000);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);
  if (!visible) return null;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
      <div style={{
        position: "absolute", top: `${fromTop}%`, left: "-15%",
        width: "220px", height: "3px",
        background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 10%, rgba(255,255,255,0.9) 60%, #fff 85%, rgba(200,220,255,0.95) 100%)",
        borderRadius: "2px",
        boxShadow: "0 0 30px rgba(255,255,255,0.8), 0 0 60px rgba(200,220,255,0.5), 0 0 100px rgba(139,92,246,0.3)",
        animation: "comet-sweep 3.2s ease-in forwards", transform: "rotate(-12deg)",
      }}>
        <div style={{
          position: "absolute", right: "-6px", top: "-9px", width: "22px", height: "22px", borderRadius: "50%",
          background: "radial-gradient(circle, #fff 0%, rgba(200,220,255,0.8) 40%, rgba(139,92,246,0.3) 70%, transparent 100%)",
          boxShadow: "0 0 25px rgba(255,255,255,0.9), 0 0 50px rgba(200,220,255,0.6), 0 0 80px rgba(139,92,246,0.4)",
        }} />
        <div style={{
          position: "absolute", right: "10px", top: "-1px", width: "300px", height: "5px",
          background: "linear-gradient(270deg, transparent 0%, rgba(139,92,246,0.2) 40%, rgba(236,72,153,0.15) 70%, transparent 100%)",
          borderRadius: "3px", transform: "translateX(-100%)", filter: "blur(2px)",
        }} />
      </div>
    </div>
  );
};

/* -------- Cloud layers -------- */
const CloudLayers = ({ opacity = 0.2 }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[3]">
    <div className="absolute rounded-full blur-3xl" style={{ width: 800, height: 130, top: "5%", left: "-15%", background: `rgba(255,255,255,${Math.min(opacity * 1.2, 0.3)})`, animation: "cloud-drift 55s linear infinite" }} />
    <div className="absolute rounded-full blur-3xl" style={{ width: 650, height: 110, top: "20%", right: "-20%", background: `rgba(255,255,255,${opacity})`, animation: "cloud-drift 70s linear infinite reverse" }} />
    <div className="absolute rounded-full blur-3xl" style={{ width: 500, height: 90, top: "40%", left: "10%", background: `rgba(255,255,255,${opacity * 0.9})`, animation: "cloud-drift 65s linear -25s infinite" }} />
    <div className="absolute rounded-full blur-3xl" style={{ width: 700, height: 100, top: "60%", right: "-10%", background: `rgba(255,255,255,${opacity * 0.75})`, animation: "cloud-drift 80s linear -40s infinite" }} />
  </div>
);

/* -------- Nebulas -------- */
const Nebulas = () => (
  <div className="nebula-layer">
    <div className="nebula-blob" style={{ width: 400, height: 400, top: "10%", left: "-5%", background: "radial-gradient(circle, rgba(59,130,246,0.3), transparent)" }} />
    <div className="nebula-blob" style={{ width: 350, height: 350, top: "50%", right: "-10%", background: "radial-gradient(circle, rgba(139,92,246,0.25), transparent)" }} />
    <div className="nebula-blob" style={{ width: 300, height: 300, bottom: "5%", left: "30%", background: "radial-gradient(circle, rgba(236,72,153,0.2), transparent)" }} />
  </div>
);

/* -------- Fireflies -------- */
const Fireflies = ({ count = 12 }) => (
  <div className="firefly-layer">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="firefly" style={{ top: `${15 + Math.random() * 70}%`, left: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 8}s` }} />
    ))}
  </div>
);

/* -------- Weather Effects (Occasional / Random) -------- */
const WEATHER_TYPES = ["clear", "rain", "snow", "aurora"];

const WeatherRain = () => {
  const drops = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 2,
    dur: 0.6 + Math.random() * 0.4,
    height: 15 + Math.random() * 20,
    opacity: 0.15 + Math.random() * 0.25,
  })), []);
  return (
    <div className="fixed inset-0 pointer-events-none z-[6] overflow-hidden">
      {drops.map((d, i) => (
        <div key={i} className="absolute" style={{
          left: `${d.left}%`, top: "-5%",
          width: "1.5px", height: `${d.height}px`,
          background: `linear-gradient(180deg, transparent, rgba(147,197,253,${d.opacity}))`,
          animation: `weather-rain-fall ${d.dur}s linear ${d.delay}s infinite`,
        }} />
      ))}
    </div>
  );
};

const WeatherSnow = () => {
  const flakes = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 6,
    dur: 5 + Math.random() * 8,
    size: 3 + Math.random() * 5,
    drift: -30 + Math.random() * 60,
    opacity: 0.3 + Math.random() * 0.5,
  })), []);
  return (
    <div className="fixed inset-0 pointer-events-none z-[6] overflow-hidden">
      {flakes.map((f, i) => (
        <div key={i} className="absolute rounded-full" style={{
          left: `${f.left}%`, top: "-3%",
          width: f.size, height: f.size,
          background: `rgba(255,255,255,${f.opacity})`,
          boxShadow: `0 0 ${f.size * 2}px rgba(255,255,255,${f.opacity * 0.5})`,
          animation: `weather-snow-fall ${f.dur}s linear ${f.delay}s infinite`,
          "--drift": `${f.drift}px`,
        }} />
      ))}
    </div>
  );
};

const WeatherAurora = () => (
  <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden">
    <div className="absolute top-0 left-0 w-full h-[50%]" style={{
      background: `linear-gradient(180deg,
        rgba(52,211,153,0.08) 0%,
        rgba(139,92,246,0.06) 25%,
        rgba(34,211,238,0.05) 50%,
        transparent 100%)`,
      animation: "weather-aurora-wave 12s ease-in-out infinite",
    }} />
    <div className="absolute top-0 left-0 w-full h-[40%]" style={{
      background: `linear-gradient(180deg,
        rgba(236,72,153,0.06) 0%,
        rgba(52,211,153,0.08) 30%,
        transparent 100%)`,
      animation: `weather-aurora-wave 15s ease-in-out -4s infinite reverse`,
    }} />
    {[0, 1, 2, 3, 4].map(i => (
      <div key={i} className="absolute" style={{
        top: `${2 + i * 6}%`,
        left: `${10 + i * 15}%`,
        width: `${120 + i * 40}px`,
        height: "3px",
        borderRadius: "50%",
        background: [
          "rgba(52,211,153,0.35)",
          "rgba(139,92,246,0.3)",
          "rgba(34,211,238,0.25)",
          "rgba(236,72,153,0.2)",
          "rgba(52,211,153,0.25)",
        ][i],
        filter: "blur(3px)",
        animation: `weather-aurora-ribbon ${6 + i * 2}s ease-in-out ${i * -1.5}s infinite`,
      }} />
    ))}
  </div>
);

const useWeatherEffect = () => {
  const [weather, setWeather] = useState("clear");
  const weatherRef = useRef(null);

  useEffect(() => {
    const pickWeather = () => {
      const roll = Math.random();
      if (roll < 0.15) setWeather("rain");
      else if (roll < 0.25) setWeather("snow");
      else if (roll < 0.38) setWeather("aurora");
      else setWeather("clear");
      weatherRef.current = setTimeout(pickWeather, 40000 + Math.random() * 50000);
    };
    weatherRef.current = setTimeout(pickWeather, 15000 + Math.random() * 20000);
    return () => { if (weatherRef.current) clearTimeout(weatherRef.current); };
  }, []);

  return weather;
};

/* ====================================================
   MAIN EXPORT: UniverseLayout
   ==================================================== */
const UniverseLayout = ({ children }) => {
  const timeSky = useSkyTheme();
  const [mouseY, setMouseY] = useState(0);
  const [appTheme, setAppThemeState] = useState(getAppTheme);
  useEffect(() => {
    const h = (e) => setAppThemeState(e?.detail || getAppTheme());
    const relearn = () => setAppThemeState(getAppTheme()); // learner switched → their theme
    window.addEventListener(APP_THEME_EVENT, h);
    window.addEventListener("active-learner-changed", relearn);
    return () => { window.removeEventListener(APP_THEME_EVENT, h); window.removeEventListener("active-learner-changed", relearn); };
  }, []);
  // A named theme overrides the time-of-day sky with its own deep palette;
  // "auto" keeps the shipped dawn→night cycle. Content is always dark glass.
  const themeCfg = getAppThemeConfig(appTheme);
  const usingNamed = appTheme !== "auto" && !!themeCfg.sky;
  const sky = usingNamed ? { period: "night", sky: themeCfg.sky, starOpacity: themeCfg.starOpacity } : timeSky;
  const isNight = sky.period === "night" || sky.period === "dusk";
  const weather = useWeatherEffect();

  useEffect(() => {
    const handler = (e) => setMouseY((e.clientY / window.innerHeight - 0.5) * 2);
    window.addEventListener("mousemove", handler, { passive: true });
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  return (
    <div className="living-world relative min-h-screen overflow-hidden">
      <MouseSparkleTrail />

      {/* LAYER 1: Far cosmic sky */}
      <div className={`fixed inset-0 pointer-events-none bg-gradient-to-b ${sky.sky} transition-colors duration-[10000ms]`} />
      <DayNightCycle />

      {/* LAYER 2: Distant stars + nebulas */}
      <div className="fixed inset-0 pointer-events-none" style={{ transform: `translateY(${mouseY * -2}px)`, transition: "transform 0.3s ease-out" }}>
        <GradientMesh />
        {sky.starOpacity > 0 && <ParallaxStars />}
        <Nebulas />
      </div>

      {/* LAYER 3: Midfield aurora / atmosphere */}
      <div className="fixed inset-0 pointer-events-none" style={{ transform: `translateY(${mouseY * -5}px)`, transition: "transform 0.3s ease-out" }}>
        <AnimatedOrbs colorsOverride={usingNamed ? themeCfg.orbColors : null} />
        <WaveRibbons />
        <LightRays />
        <PulsingRings />
        <HorizonGlow />
      </div>

      {/* LAYER 3.5: Floating planets */}
      <FloatingPlanets />

      {/* LAYER 4: Weather effects (occasional) */}
      {weather === "rain" && <WeatherRain />}
      {weather === "snow" && <WeatherSnow />}
      {weather === "aurora" && <WeatherAurora />}

      {/* LAYER 5: Foreground sparkles / meteors */}
      <div className="fixed inset-0 pointer-events-none" style={{ transform: `translateY(${mouseY * -8}px)`, transition: "transform 0.3s ease-out" }}>
        <MeteorTrails />
        <BoldSparkles isNight={isNight} />
        <ShootingStars />
        {isNight && <Fireflies count={15} />}
        <ForegroundSparkles />
      </div>
      <GiantComet />
      <CloudLayers opacity={isNight ? 0.08 : 0.2} />

      {/* PAGE CONTENT */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default UniverseLayout;
